import React, { useEffect, useMemo, useState } from "react";
import Select from 'react-select';

import PanelTemplate from "../../templates/Panel";
import Table from "../../components/Table";
import Box from "../../components/Box";
import Button from "../../components/Button";
import PageHeader from "../../components/PageHeader";
import FilterSimple, { filterSelectStyles } from "../../components/FilterSimple";
import DotStatus from "../../components/DotStatus";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import moment from "moment";
import { createOptions } from "../../helpers/selects";
import { hasPermission, hasPlanFeature } from "../../services/auth";
import { ModalDelete } from "../../components/ModalDelete";
import Pagination from "../../components/Pagination";
import { ModalFilter } from "../Product/ModalFilter";
import ButtonDropdown from "../../components/ButtonDropdown";
import ModalUpdatePricesBulk from "../../components/ModalUpdatePricesBulk";
import ModalUpdateStockBulk from "../../components/ModalUpdateStockBulk";
import ModalBulkEditAttributes from "../../components/ModalBulkEditAttributes";

import SuspensionAlert from "../../components/SuspensionAlert";

import SortHeader from "../../components/SortHeader";
import { convertIntToMoney } from "../../helpers/conversions";
import { getSituation } from "../../services/auth";
import ProductPriceAlert from "../../components/ProductPriceAlert";
import PlanFeatureUpgradeButton from "../../components/PlanFeatureUpgradeButton";
import PlanFeatureUpgradeModal from "../../components/PlanFeatureUpgradeModal";


const indexMap = [
    { index: 'Nome', value: 'name' },
    ...(hasPermission('show_product_supplier_price_in_budget')
        ? [{ index: 'Preço a partir de', value: 'price' }]
        : []),
    { index: 'Estoque total', value: 'stock' },
    { index: 'SKU', value: 'sku' },
    { index: 'Última atualização', value: 'updated_at' },
    { index: 'Cadastrado em', value: 'created_at' },
    { index: 'Site principal', value: 'exclusive' },
];

const productSearchParams = [
    {name: 'Nome', key: '[name]', operation: 'contain', type: 'text' },
    {name: 'SKU', key: '[sku]', operation: 'contain', type: 'text' },
    {name: 'Origem', key: '[origin]', operation: 'contain', type: 'text' }
];

const getProductSearchParam = field => (
    productSearchParams.find(param => param.key === field) || productSearchParams[0]
);

const normalizePage = page => {
    const parsedPage = parseInt(page, 10);
    return Number.isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage;
};

const getParam = (params, key) => {
    const value = params.get(key);
    return value === '' ? null : value;
};

const parseProductListFilters = search => {
    const params = new URLSearchParams(search);
    const searchParam = getProductSearchParam(params.get('search_field'));

    return {
        page: normalizePage(params.get('page')),
        searchField: searchParam.key,
        searchOperation: params.get('search_operation') || searchParam.operation || 'contain',
        search: params.get('search') || '',
        category: getParam(params, 'category'),
        ordenationItem: params.get('sort') || 'created_at',
        orientation: params.get('orientation') || 'desc',
        productId: getParam(params, 'product_id'),
        name: getParam(params, 'name'),
        mainSite: getParam(params, 'main_site'),
        quotable: getParam(params, 'quotable'),
        sku: getParam(params, 'sku'),
        companySku: getParam(params, 'company_sku'),
        companyId: getParam(params, 'supplier_id'),
        featured: getParam(params, 'featured'),
        recent: getParam(params, 'recent'),
        tagId: getParam(params, 'tag_id'),
        image: getParam(params, 'image'),
        customization: getParam(params, 'customization'),
        brandId: getParam(params, 'brand_id'),
        minPrice: getParam(params, 'min_price'),
        maxPrice: getParam(params, 'max_price'),
        minStock: getParam(params, 'min_stock'),
        minQuantity: getParam(params, 'min_quantity'),
    };
};

const hasValue = value => value !== null && value !== undefined && value !== '';

const appendProductFilter = (params, field, operation, value) => {
    if (hasValue(value)) {
        params.append(`filters[0]${field}[${operation}]`, value);
    }
};

const buildProductApiQuery = (filters, includePage = true) => {
    const params = new URLSearchParams();

    if (filters.productId) {
        appendProductFilter(params, '[id]', 'equal', filters.productId);
    }

    if (filters.search.trim() !== '') {
        appendProductFilter(params, filters.searchField, filters.searchOperation, filters.search.trim());
    }

    appendProductFilter(params, '[name]', 'contain', filters.name);
    // Site principal é o `exclusive` lido ao contrário.
    appendProductFilter(params, '[exclusive]', 'equal', hasValue(filters.mainSite) ? (filters.mainSite === '1' ? '0' : '1') : null);
    appendProductFilter(params, '[quotable]', 'equal', filters.quotable);
    appendProductFilter(params, '[sku]', 'contain', filters.sku);
    appendProductFilter(params, '[company_sku]', 'contain', filters.companySku);
    appendProductFilter(params, '[company_id]', 'equal', filters.companyId);
    appendProductFilter(params, '[featured]', 'equal', filters.featured);
    appendProductFilter(params, '[recent]', 'equal', filters.recent);
    appendProductFilter(params, '[tag_id]', 'equal', filters.tagId);
    appendProductFilter(params, '[image]', 'equal', filters.image);
    appendProductFilter(params, '[price]', 'bigger_or_equal', filters.minPrice);
    appendProductFilter(params, '[price]', 'less_or_equal', filters.maxPrice);
    appendProductFilter(params, '[stock]', 'bigger_or_equal', filters.minStock);
    appendProductFilter(params, '[min_quantity]', 'bigger_or_equal', filters.minQuantity);
    appendProductFilter(params, '[related][categories.id]', 'equal', filters.category);
    appendProductFilter(params, '[related][customizations.id]', 'equal', filters.customization);
    appendProductFilter(params, '[related][brands.id]', 'equal', filters.brandId);

    params.set('ordenation_item', filters.ordenationItem);
    params.set('orientation', filters.orientation);

    if (includePage) {
        params.set('page', filters.page);
    }

    return params.toString();
};

const buildProductListSearch = filters => {
    const params = new URLSearchParams();

    if (filters.page > 1) params.set('page', filters.page);
    if (filters.search.trim() !== '') {
        params.set('search_field', filters.searchField);
        params.set('search_operation', filters.searchOperation);
        params.set('search', filters.search.trim());
    }
    if (hasValue(filters.category)) params.set('category', filters.category);
    if (filters.ordenationItem !== 'created_at') params.set('sort', filters.ordenationItem);
    if (filters.orientation !== 'desc') params.set('orientation', filters.orientation);
    if (hasValue(filters.productId)) params.set('product_id', filters.productId);
    if (hasValue(filters.name)) params.set('name', filters.name);
    if (hasValue(filters.mainSite)) params.set('main_site', filters.mainSite);
    if (hasValue(filters.quotable)) params.set('quotable', filters.quotable);
    if (hasValue(filters.sku)) params.set('sku', filters.sku);
    if (hasValue(filters.companySku)) params.set('company_sku', filters.companySku);
    if (hasValue(filters.companyId)) params.set('supplier_id', filters.companyId);
    if (hasValue(filters.featured)) params.set('featured', filters.featured);
    if (hasValue(filters.recent)) params.set('recent', filters.recent);
    if (hasValue(filters.tagId)) params.set('tag_id', filters.tagId);
    if (hasValue(filters.image)) params.set('image', filters.image);
    if (hasValue(filters.customization)) params.set('customization', filters.customization);
    if (hasValue(filters.brandId)) params.set('brand_id', filters.brandId);
    if (hasValue(filters.minPrice)) params.set('min_price', filters.minPrice);
    if (hasValue(filters.maxPrice)) params.set('max_price', filters.maxPrice);
    if (hasValue(filters.minStock)) params.set('min_stock', filters.minStock);
    if (hasValue(filters.minQuantity)) params.set('min_quantity', filters.minQuantity);

    return params.toString();
};

const countActiveProductFilters = filters => ([
    filters.search.trim(),
    filters.category,
    filters.productId,
    filters.name,
    filters.mainSite,
    filters.quotable,
    filters.sku,
    filters.companySku,
    filters.companyId,
    filters.featured,
    filters.recent,
    filters.tagId,
    filters.image,
    filters.customization,
    filters.brandId,
    filters.minPrice || filters.maxPrice,
    filters.minStock,
    filters.minQuantity,
].filter(hasValue).length);

export const ProductList = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({header: [], rows: []});
    const [products, setProducts] = useState([]);

    const [categories, setCategories] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [totalResults, setTotalResults] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    const [selectedIds, setSelectedIds] = useState([]);
    const [showConfirmMultipleDelete, setShowConfirmMultipleDelete] = useState(false);
    const [showUpdatePricesModal, setShowUpdatePricesModal] = useState(false);
    const [showUpdateStocksModal, setShowUpdateStocksModal] = useState(false);
    const [upgradeFeature, setUpgradeFeature] = useState(null);
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [loadingAllIds, setLoadingAllIds] = useState(false);

    const [showFilter, setShowFilter] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const productFilters = useMemo(() => parseProductListFilters(location.search), [location.search]);
    const activeFilterCount = useMemo(() => countActiveProductFilters(productFilters), [productFilters]);

    const handleOrdenation = (orientationItem, oredenation) => {
        updateProductListUrl({
            ordenationItem: orientationItem,
            orientation: oredenation,
        });
    };

    const handleSetCustomFilters = value => {
        updateProductListUrl(value);
    }

    useEffect(() => {
        getCategories();
    }, []);

    useEffect(() => {
        getProducts();
    }, [location.search]);

    useEffect(() => {
        generateData();
    }, [products, location.search]);

    const getProducts = () => {
        setProducts([]);
        setLoading(true);
        api.get(`/product/paginate?${buildProductApiQuery(productFilters)}`).then(res => {
            setProducts(res.data.resources.data);
            setCurrentPage(res.data.resources.pagination.current_page);
            setLastPage(res.data.resources.pagination.last_page)
            setPerPage(res.data.resources.pagination.per_page)
            setTotalResults(res.data.resources.pagination.total)
            setHasNextPage(res.data.resources.pagination.next_page_url !== null);
            setHasPrevPage(res.data.resources.pagination.prev_page_url !== null);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    const getCategories = () => {
        api.get(`/category`).then(res => {
            let toCategories = [];
            res.data.resources.map(category => {
                toCategories.push(category);
                category.categories.map(subcategory => toCategories.push({
                    ...subcategory,
                    name: `${category.name} > ${subcategory.name}`
                }));
            });
            setCategories(toCategories);
        });
    }

    const generateData = () => {
        
        const header = [
        'SKU',
        'Imagem',
        'Nome',
        ...(hasPermission('show_product_supplier_price_in_budget')
            ? ['Preço a partir de']
            : []),
        'Estoque total',
        'Última atualização',
        'Cadastrado em',
        'Site principal',
        'Origem',
        ];

        const rows = [];

        products.map(product => {
            console.log(product);
            let image =  <img src={ require(`../../assets/images/svgs/default.svg`).default } />;
            const returnTo = encodeURIComponent(`/product/list${location.search}`);
            if(product.image){
                image = <img src={product.image.image_url.small} />;
            }

            rows.push({
                data: [
                    product.sku,
                    image,
                    product.personalized_brand
                        ? <span className="product-list-name">
                            {product.name}
                            <span className="product-list-personalized">Personalizado · {product.personalized_brand.name}</span>
                        </span>
                        : product.name,
                    ...(hasPermission('show_product_supplier_price_in_budget')
                    ? [hasPlanFeature('calculation')
                        ? <>{product?.price ? convertIntToMoney(product.price, true) : 'R$ 0,00'} <ProductPriceAlert product={product} /></>
                        : <PlanFeatureUpgradeButton onClick={() => setUpgradeFeature('calculation')} stopPropagation />
                    ] : []),
                    hasPlanFeature('stock')
                        ? (product?.stock ?? 0).toLocaleString('pt-BR')
                        : <PlanFeatureUpgradeButton onClick={() => setUpgradeFeature('stock')} stopPropagation />,
                    moment(product.updated_at).format('DD/MM/YYYY HH:mm'),
                    moment(product.created_at).format('DD/MM/YYYY HH:mm'),
                    product.exclusive ? 'Não' : 'Sim',
                    product.personalized_brand ? 'Personalizado' : (product.origin === 'site' ? 'Site' : 'Integração')
                ],
                to: product.personalized_brand
                    ? `/brand/${product.personalized_brand.id}/personalized/${product.id}?return_to=${returnTo}`
                    : `/product/edit/${product.id}?return_to=${returnTo}`,
                id: product.id
            })
        });

        setData({header, rows});
    }

    const deleteMultipleProducts = () => {
        setLoading(true);

        api.delete(`/product/multiple`, {
            data: {
                ids: selectedIds
            }
        }).then(res => {
            setSelectedIds([]);
            getProducts();
            setShowConfirmMultipleDelete(false);
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const updateProductListUrl = (changes, resetPage = true) => {
        const nextFilters = {
            ...productFilters,
            ...changes,
            page: resetPage ? 1 : (changes.page || productFilters.page),
        };
        const search = buildProductListSearch(nextFilters);

        navigate({
            pathname: location.pathname,
            search: search ? `?${search}` : '',
        });
    }

    const handleSetFilter = (value, metadata) => {
        updateProductListUrl({
            searchField: metadata?.field || productFilters.searchField,
            searchOperation: metadata?.operation || productFilters.searchOperation,
            search: metadata?.value ?? '',
            productId: null,
        });
    }

    const handleSelectAllResults = () => {
    if (selectedIds.length === totalResults) {
        setSelectedIds([]);
        return;
    }

    setLoadingAllIds(true);

    api.get(`/product/ids?${buildProductApiQuery(productFilters, false)}`)
        .then(res => {
            setSelectedIds(res.data.resources.data);
        })
        .catch(renderError)
        .finally(() => setLoadingAllIds(false));
};

    const handleSetCategory = category => {
        updateProductListUrl({ category, productId: null });
    }

    const handleSetPage = page => {
        updateProductListUrl({ page }, false);
    }

    const handleBulkPriceUpdate = () => {
        if (!hasPlanFeature('calculation')) {
            setUpgradeFeature('calculation');
            return;
        }

        setShowUpdatePricesModal(true);
    };

    const handleBulkStockUpdate = () => {
        if (!hasPlanFeature('stock')) {
            setUpgradeFeature('stock');
            return;
        }

        setShowUpdateStocksModal(true);
    };

    return (
        <>
            <ModalDelete
                show={showConfirmMultipleDelete}
                text={<p>Tem certeza que deseja apagar <strong>{selectedIds.length} {selectedIds.length > 1 ? 'produtos' : 'produto'}</strong>?</p>}
                action={deleteMultipleProducts}
                loading={loading}
                close={() => setShowConfirmMultipleDelete(false)}
            />
            
            <ModalUpdatePricesBulk
                show={showUpdatePricesModal}
                close={() => setShowUpdatePricesModal(false)}
                refresh={getProducts}
            />
            <ModalUpdateStockBulk
                show={showUpdateStocksModal}
                close={() => setShowUpdateStocksModal(false)}
                refresh={getProducts}
            />
            <PlanFeatureUpgradeModal
                feature={upgradeFeature}
                show={upgradeFeature !== null}
                close={() => setUpgradeFeature(null)}
            />
            <ModalBulkEditAttributes
                show={showBulkEditModal}
                close={() => setShowBulkEditModal(false)}
                selectedIds={selectedIds}
                refresh={getProducts}
            />
            <PanelTemplate id="product-list">
                <ModalFilter
                    showFilter={showFilter}
                    setShowFilter={setShowFilter}
                    filters={productFilters}
                    setCustomFilters={handleSetCustomFilters}
                />
                <PageHeader
                    left={
                        <>
                            <FilterSimple 
                                params={productSearchParams}
                                filter={handleSetFilter}
                                externalValue={productFilters.search}
                                externalOption={productFilters.searchField}
                            />
                            <div className="filter-select">
                                <Select
                                    placeholder="Escolha uma categoria..."
                                    options={[{label: 'Todas as categorias', value: null},{label: 'Sem categoria', value: 'without_categories'}, ...createOptions(categories, 'name', 'id')]}
                                    onChange={e => handleSetCategory(e.value)}
                                    value={
                                        productFilters.category === 'without_categories'
                                            ? { label: 'Sem categoria', value: 'without_categories' }
                                            : productFilters.category === null
                                            ? { label: 'Todas as categorias', value: null }
                                            : {
                                                label: categories.find(c => String(c.id) === String(productFilters.category))?.name ?? 'Categoria selecionada',
                                                value: productFilters.category
                                            }
                                    }
                                    styles={filterSelectStyles}
                                />
                            </div>
                        </>
                    }

                    right={
                        <>
                            {hasPermission('delete_product') && selectedIds.length > 0 &&
                                <Button 
                                type={`secondary`} 
                                size={`small`} 
                                svg={`delete`}
                                text={`Excluir ${selectedIds.length} selecionados`} 
                                action={() => setShowConfirmMultipleDelete(true)}  
                                />
                            }
                            
                            <Button type={`secondary`} size={`small`} text={`Filtrar`} svg="filter" badge={activeFilterCount} action={() => setShowFilter(true)} />
                            {hasPermission('edit_product') &&
                                <ButtonDropdown
                                    type="primary"
                                    text="Ações em massa"
                                    svg="crm"
                                    actions={[
                                        {
                                            text: 'Atualizar preços em massa',
                                            action: handleBulkPriceUpdate
                                        },
                                        {
                                            text: 'Atualizar estoques em massa',
                                            action: handleBulkStockUpdate
                                        },
                                        {
                                            text: `Editar atributos em massa (${selectedIds.length} ${selectedIds.length === 1 ? 'produto' : 'produtos'})`,
                                            action: () => setShowBulkEditModal(true)
                                        }
                                    ]}
                                    size="small"
                                />
                            }
                            {hasPermission('create_product') &&
                                <Button type={`primary`} size={`small`} text={`Cadastrar produto`} svg="plus-white" link={`/product/create`}  />
                            }
                            {/* <Button type={`secondary`} size={`small`} text={`Filtrar`} svg="filter"  /> */}
                        </>
                    }
                />
                
                <Box className={`no-padding with-pagination`}>
                    <Table 
                        header={data.header} 
                        rows={data.rows} 
                        loading={loading || loadingAllIds}
                        checks={true} 
                        selectedIds={selectedIds}
                        setSelectedIds={setSelectedIds}
                        totalCount={totalResults}
                        onSelectAllResults={handleSelectAllResults}
                        sortOrdenationAction={(index) => (
                            <SortHeader
                                index={index}
                                ordenationItem={productFilters.ordenationItem}
                                orientation={productFilters.orientation}
                                onSort={handleOrdenation}
                                indexMap={indexMap}
                            />
                        )}
                    />
                </Box>
                {!loading &&
                    <Pagination
                        currentPage={currentPage}
                        lastPage={lastPage}
                        perPage={perPage}
                        totalResults={totalResults}
                        hasNextPage={hasNextPage}
                        hasPrevPage={hasPrevPage}
                        setCurrentPage={handleSetPage}
                    />
                }
            </PanelTemplate>
        </>
    );
}

export default ProductList;
